// ./component/DoctorChat.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { FiChevronLeft } from "react-icons/fi";
import { IoIosSend } from "react-icons/io";
import "./HospitalChat.css";
import { AppContext } from "../../appContext";
import createSocket from "../../Pages/Socket/socket";
import { EditProfileLoader, Loader } from "../common/loader";
import { LuCheckCheck } from "react-icons/lu";
import { LuCheck } from "react-icons/lu";
import { IoCheckmarkDoneOutline, IoCheckmarkOutline } from "react-icons/io5";

/**
 * Doctor-side chat modal (doctor ↔ hospital)
 * UI-only updates for responsive popup/fullscreen; logic unchanged.
 */
export default function DoctorChat({ closeFunction, doctorDetails, consultationId }) {
  const myRole = "doctor";
  const { userDetails } = useContext(AppContext);
  const socket = useRef(null);

  const [messages, setMessages] = useState([]); // [{...,_clientRead:boolean}]
  const [text, setText] = useState("");
  const [joined, setJoined] = useState(false);
  const [participants, setParticipants] = useState({ doctor: null, hospital: null });
  const listRef = useRef(null);

  // ---- Resolve PROFILE ids (not user ids) ----
  const doctorProfileId =
    (typeof window !== "undefined" && window.__DOCTOR_PROFILE_ID) ||
    doctorDetails?._id ||
    "REPLACE_WITH_DOCTOR_PROFILE_ID";

  const hospitalProfileId =
    doctorDetails?.hospitalId ||
    (typeof window !== "undefined" && window.__HOSPITAL_PROFILE_ID) ||
    "REPLACE_WITH_HOSPITAL_PROFILE_ID";

  // Build payload for socket
  const payload = useMemo(
    () => ({ consultationId, hospitalId: hospitalProfileId, doctorId: doctorProfileId }),
    [consultationId, hospitalProfileId, doctorProfileId]
  );

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  // Helpers
  const initials = (name = "") => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    const a = parts[0]?.[0] || "";
    const b = parts[1]?.[0] || parts[0]?.[1] || "";
    return (a + b).toUpperCase() || "??";
  };

  // ---- Read-ack queue (IDs of hospital messages I viewed) ----
  const readQueueRef = useRef(new Set());
  const readTimerRef = useRef(null);

  const flushReadAcks = () => {
    if (!socket.current) return;
    const ids = Array.from(readQueueRef.current);
    if (!ids.length) return;
    readQueueRef.current.clear();
    socket.current.emit("markRead", { consultationId, ids });
  };

  const scheduleFlush = () => {
    if (readTimerRef.current) clearTimeout(readTimerRef.current);
    readTimerRef.current = setTimeout(flushReadAcks, 150);
  };

  // Enqueue not-yet-read messages from the OTHER party (hospital)
  const enqueueUnreadFromOther = (list) => {
    let added = false;
    for (const m of list) {
      const fromOther = m.user?.name && m.user.name !== myRole; // hospital → doctor
      const alreadyReadByMe = m.read?.[myRole] === true; // read.doctor
      if (fromOther && !alreadyReadByMe) {
        readQueueRef.current.add(String(m._id));
        added = true;
      }
    }
    if (added) scheduleFlush();
  };

  // ---- Socket bootstrap & listeners ----
  useEffect(() => {
    if (!userDetails || !consultationId) return;

    // create/connect
    socket.current = createSocket(userDetails);
    const s = socket.current;
    s.connect();

    const bootstrap = () => {
      s.emit("createConsultationRoom", payload);
      s.emit("joinConsultation", { consultationId });
      s.emit("getHistory", { consultationId, limit: 50 });
    };

    const onConnect = () => bootstrap();

    const onParticipants = ({ doctor, hospital }) => setParticipants({ doctor, hospital });
    const onJoined = () => setJoined(true);

    // History:
    // Doctor ticks ✓✓ when HOSPITAL has read → use read.hospital on my messages
    const onHistory = ({ messages: list = [] }) => {
      const seeded = list.map((m) => ({
        ...m,
        _clientRead: m.user?.name === myRole ? !!m.read?.hospital : false,
      }));
      setMessages(seeded);
      enqueueUnreadFromOther(list); // mark incoming as read
    };

    const onMessage = ({ message }) => {
      const isMe = message.user?.name === myRole;
      const enriched = {
        ...message,
        _clientRead: isMe ? !!message.read?.hospital : false,
      };
      setMessages((prev) => [...prev, enriched]);

      if (!isMe) enqueueUnreadFromOther([message]);
    };

    // Conversation-level snapshot:
    // When HOSPITAL unread === 0, hospital has seen my latest → flip my ticks locally
    const onUnreadUpdated = ({ unread }) => {
      if (typeof unread?.hospital === "number" && unread.hospital === 0) {
        setMessages((prev) =>
          prev.map((m) => (m.user?.name === myRole ? { ...m, _clientRead: true } : m))
        );
      }
    };

    // Per-message ack: server tells which IDs were read and by whom
    const onMessagesRead = ({ consultationId: cid, role, ids }) => {
      if (cid !== consultationId) return;
      if (role === "hospital" && Array.isArray(ids) && ids.length) {
        const idSet = new Set(ids.map(String));
        setMessages((prev) =>
          prev.map((m) =>
            m.user?.name === myRole && idSet.has(String(m._id))
              ? { ...m, _clientRead: true }
              : m
          )
        );
      }
    };

    const onChatClosed = () => setJoined(false);
    const onErr = (e) => console.error("chatError:", e?.message || e);

    // register
    s.on("connect", onConnect);
    s.on("participants", onParticipants);
    s.on("joinedConsultation", onJoined);
    s.on("history", onHistory);
    s.on("message", onMessage);
    s.on("unreadUpdated", onUnreadUpdated);
    s.on("messagesRead", onMessagesRead);
    s.on("chatClosed", onChatClosed);
    s.on("chatError", onErr);

    // ensure a connection exists (handles popup reopen without page reload)
    if (!s.connected) s.connect();
    else bootstrap();

    return () => {
      try {
        s.emit("leaveConsultation", { consultationId });
      } catch {}
      s.off("connect", onConnect);
      s.off("participants", onParticipants);
      s.off("joinedConsultation", onJoined);
      s.off("history", onHistory);
      s.off("message", onMessage);
      s.off("unreadUpdated", onUnreadUpdated);
      s.off("messagesRead", onMessagesRead);
      s.off("chatClosed", onChatClosed);
      s.off("chatError", onErr);
      if (readTimerRef.current) clearTimeout(readTimerRef.current);
      s.disconnect();
      socket.current = null;
    };
  }, [userDetails, consultationId, payload, myRole]);

  // ---- Send flows ----
  const send = () => {
    const value = (text || "").trim();
    if (!value || !joined || !socket.current) return;
    socket.current.emit("sendMessage", { consultationId, text: value });
    setText("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Utility to format date labels
  const formatDateLabel = (dateStr) => {
    const d = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const isToday = d.toDateString() === today.toDateString();
    const isYesterday = d.toDateString() === yesterday.toDateString();

    if (isToday) return "Today";
    if (isYesterday) return "Yesterday";

    return d.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Group messages by date
  const groupedMessages = messages.reduce((acc, msg) => {
    const label = formatDateLabel(msg.createdAt);
    if (!acc[label]) acc[label] = [];
    acc[label].push(msg);
    return acc;
  }, {});

  // Header shows the HOSPITAL (peer) on the doctor screen
  const hospitalName = participants?.hospital?.displayName || "Hospital";
  const hospitalAvatar = participants?.hospital?.avatar || null;

  return (
    <section className="chat-overlay" onClick={closeFunction}>
      <div className="chat-container" onClick={(e) => e.stopPropagation()}>
        <header className="chat-header">
          <FiChevronLeft onClick={closeFunction} className="back-icon" />
          {/* <div className="chat-title">Consultation Chat</div> */}

          <div className="peer">
            {hospitalAvatar ? (
              <img className="avatar-chat" src={`${process.env.REACT_APP_API_URL}/${hospitalAvatar}`} alt="hospital" />
            ) : (
              <div className="avatar-chat">{initials(hospitalName)}</div>
            )}
            <span className="peer-name">{hospitalName}</span>
          </div>
        </header>

        {joined ? (
          <>
            <div ref={listRef} className="chat-messages" style={{display: "none"}}>
              {messages.map((m) => {
                const isMe = m.user?.name === myRole;

                // Per-bubble avatar: doctor (me) on right, hospital (peer) on left
                // const avatarUrl = isMe ? participants?.doctor?.avatar || null : hospitalAvatar;
                // const fallbackInitials = isMe
                //   ? initials(participants?.doctor?.displayName || "Me")
                //   : initials(hospitalName);

                return (
                  <div key={m._id} className={`row ${isMe ? "me" : "other"}`}>
                    
                    <div className="bubble">
                      <div>{m.text}</div>
                      <div className="timeTicks">
                        <span>
                          {new Date(m.createdAt).toLocaleTimeString("en-US", {
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </span>

                        {isMe && <span className="tick">{m._clientRead ? (
                             <IoCheckmarkDoneOutline color="#25D366" size={18} />
                            ) : (
                              <IoCheckmarkOutline color="#7c7f86ff" size={18} />
                            )}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
              
            </div>

            <div ref={listRef} className="chat-messages">
              {Object.entries(groupedMessages).map(([dateLabel, list]) => (
                <div key={dateLabel}>
                  
                  <div className='date-badge-container'>
                    <div className="date-badge">{dateLabel}</div>
                  </div>

                  {list.map((m) => {
                    const isMe = m.user?.name === myRole;
                    return (
                      <div key={m._id} className={`row ${isMe ? "me" : "other"}`}>
                        <div className="bubble">
                          <div>{m.text}</div>
                          <div className="timeTicks">
                            <span>
                              {new Date(m.createdAt).toLocaleTimeString("en-US", {
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: true,
                              })}
                            </span>
                            {isMe && (
                              <span className="tick">
                                {m._clientRead ? (
                                  <IoCheckmarkDoneOutline color="#25D366" size={18} />
                                ) : (
                                  <IoCheckmarkOutline color="#7c7f86ff" size={18} />
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>


            <div className="chat-input">
              <textarea
                rows={1}
                placeholder={"Type a message…"}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <button onClick={send} className="send-btn" disabled={!joined || !text.trim()}>
                <IoIosSend size={20} />
                <span className="send-label">Send</span>
              </button>
            </div>
          </>
        ) : (
          <div className="loading-wrap">
            <Loader />
          </div>
        )}
      </div>
    </section>
  );
}
